import React, {
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { ContextUserProfile } from '../../../contexts/UserProfileContext';
import { capitalize } from '../../../utils/usefulFunctions';
import { cachedApi } from '../../../services/api';
import { debounce } from '@mui/material';
import endpoints from '../../../utils/commonVariables/endpoints';

const getLabelKey = (arrayOptions, key) => {
    return arrayOptions.map(opt => {
        return { ...opt, label: opt[key] || opt.Caption || opt.Name };
    });
};

const LoadedOptionsTypeahead = props => {
    const {
        labelText,
        optionsLabelKey = 'label',
        handleChange,
        id,
        errorMessage,
        infoMessage,
        required,
        idValue,
        value,
        endpoint,
        odataAdditionalFilter,
        ...TypeaheadProps
    } = props;
    const { translate } = useContext(ContextUserProfile);
    const label = capitalize(translate(labelText));
    const [inputValue, setInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState(null); // Estado para la opción seleccionada
    const [optionsTypeahead, setOptionstypeahead] = useState([]);
    const [loading, setLoading] = useState(false); // Estado para controlar la carga
    let active = useRef(true);
    const loading_options = capitalize(translate('loading_options'));
    const no_results = capitalize(translate('no_results'));
    const start_write = capitalize(translate('start_write'));

    // Memoizar la función getOptions
    const getOptions = useCallback(
        async filterValue => {
            if (!filterValue) {
                setOptionstypeahead([]);
                setLoading(false);
                return;
            }

            let filterEndpoint = '';
            if (odataAdditionalFilter) {
                const additionalFilter = `${odataAdditionalFilter.key} ${odataAdditionalFilter.operator} ${odataAdditionalFilter.value}`;
                filterEndpoint = `${endpoint}&$filter=(substringof('${filterValue}', ${optionsLabelKey})and(${additionalFilter}))`;
            } else {
                filterEndpoint = `${endpoint}&$filter=substringof('${filterValue}', ${optionsLabelKey})`;
            }

            try {
                const { data } = await cachedApi(
                    `${endpoints.odataService}${filterEndpoint}`
                );
                if (active.current) {
                    setOptionstypeahead(
                        getLabelKey(data.value, optionsLabelKey)
                    );
                }
            } catch (error) {
                console.error('Error al obtener las opciones:', error);
                setOptionstypeahead([]); // Limpiar opciones en caso de error
            } finally {
                setLoading(false);
            }
        },
        [endpoint, optionsLabelKey, odataAdditionalFilter]
    ); // Memoizar con las dependencias adecuadas

    // Memoizar la función debounceGetOptions con la referencia de getOptions
    const debounceGetOptions = useCallback(debounce(getOptions, 500), [
        getOptions,
    ]);

    useEffect(() => {
        if (!selectedOption) {
            debounceGetOptions(inputValue);
        }

        return () => {
            debounceGetOptions.clear(); // Limpiar el debounce al desmontar
        };
    }, [inputValue, selectedOption, debounceGetOptions]); // Incluir debounceGetOptions en las dependencias

    const handleInputChange = (e, newInputValue) => {
        setSelectedOption(null);
        setInputValue(newInputValue);
        setLoading(true); // Mostrar el estado de carga inmediatamente
    };

    const noOptionsText = errorMessage
        ? errorMessage
        : loading
        ? `${loading_options}...`
        : inputValue && optionsTypeahead.length === 0
        ? no_results
        : `${start_write}...`;

    return (
        <Stack width={'100%'} height={'67px'}>
            <Autocomplete
                disablePortal
                id={label}
                options={optionsTypeahead}
                sx={{ width: '100%' }}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={label}
                        size='small'
                        error={!!errorMessage}
                        required={required}
                    />
                )}
                onChange={(e, opt) => {
                    e.target.name = id;
                    e.target.value = opt ? parseInt(opt?.Id) : 0;
                    handleChange(e);
                    setSelectedOption(opt); // Actualizar el estado con la opción seleccionada
                }}
                inputValue={inputValue}
                onInputChange={handleInputChange} // Lógica de cambio de entrada
                noOptionsText={noOptionsText}
                value={
                    value
                        ? value
                        : selectedOption ||
                          optionsTypeahead.find(
                              opt => parseInt(opt.Id) === parseInt(idValue)
                          ) ||
                          null
                }
                {...TypeaheadProps}
            />
            <p className='input__error-message'>{translate(errorMessage)}</p>
            {infoMessage && (
                <p className='input__info-message'>
                    ⓘ {translate(infoMessage)}
                </p>
            )}
        </Stack>
    );
};

export default LoadedOptionsTypeahead;
